import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ASSESSMENT_QUESTIONS } from "@/data/quiz";
import { getUser, setQuizState } from "@/lib/storage";
import { EXPOSURE_META, fmtDate } from "@/lib/exposure";
import { BrandHeader } from "@/components/BrandHeader";
import { LoadingScreen } from "@/components/LoadingScreen";

// Modes: 'intro' | 'question' | 'loading' | 'report' | 'past'

export const Quiz = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("intro");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [draft, setDraft] = useState("");
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [pastReport, setPastReport] = useState(null);
  const [pastDate, setPastDate] = useState(null);

  useEffect(() => {
    if (!getUser()) { navigate("/", { replace: true }); return; }
    refreshHistory();
  }, [navigate]);

  const refreshHistory = async () => {
    try {
      const res = await api.get("/assessment/history");
      setHistory(res.data?.history || []);
    } catch { /* silently ignore — history is enhancement, not core */ }
  };

  const total = ASSESSMENT_QUESTIONS.length;
  const q = ASSESSMENT_QUESTIONS[idx];

  const start = () => {
    setMode("question");
    setIdx(0);
    setAnswers({});
    setDraft("");
    setReport(null);
  };

  const viewPast = async (id) => {
    try {
      const res = await api.get(`/assessment/${id}`);
      setPastReport(res.data.report);
      setPastDate(res.data.created_at);
      setMode("past");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Couldn't load that past Priority Map. Try again.");
    }
  };

  const next = async () => {
    if (!draft) return;
    const newAnswers = { ...answers, [q.id]: draft };
    setAnswers(newAnswers);

    if (idx + 1 >= total) {
      setMode("loading");
      try {
        const res = await api.post(`/assessment/report`, newAnswers);
        setReport(res.data.report);
        setQuizState({ completed: true, completedAt: new Date().toISOString() });
        setMode("report");
        refreshHistory();
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't generate your priority map. Please try again.");
        setMode("question");
      }
    } else {
      setIdx(idx + 1);
      setDraft("");
    }
  };

  const back = () => {
    if (idx === 0) { setMode("intro"); return; }
    const prevIdx = idx - 1;
    const prevQ = ASSESSMENT_QUESTIONS[prevIdx];
    setIdx(prevIdx);
    setDraft(answers[prevQ.id] || "");
  };

  const progress = Math.round((idx / total) * 100);

  return (
    <div data-testid="quiz-page">
      <BrandHeader />

      {mode === "intro" && (
        <AssessmentIntro
          onStart={start}
          history={history}
          onViewPast={viewPast}
        />
      )}

      {mode === "question" && (
        <section style={{ padding: "2rem 0 4rem" }}>
          <div className="container-narrow">
            <p className="eyebrow" data-testid="quiz-progress-label">
              Question {idx + 1} of {total}
            </p>
            <div className="progress-bar" style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}>
              <span style={{ width: `${progress}%` }} />
            </div>

            <h2 style={{ fontSize: "clamp(1.3rem, 3.5vw, 1.7rem)", margin: "0 0 0.6rem", lineHeight: 1.25 }} data-testid="quiz-question-text">
              {q.question}
            </h2>
            {q.helper && (
              <p style={{ color: "var(--cobalt)", fontSize: "0.92rem", margin: "0 0 1.25rem" }}>
                {q.helper}
              </p>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem", marginBottom: "1.5rem" }}>
              {q.options.map((opt, i) => {
                const letter = String.fromCharCode(65 + i);
                const isSelected = draft === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    className={`option-btn ${isSelected ? "selected" : ""}`}
                    onClick={() => setDraft(opt.id)}
                    data-testid={`quiz-option-${q.id}-${opt.id}`}
                  >
                    <span className="option-letter">{letter}</span>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-yellow"
                onClick={next}
                disabled={!draft}
                data-testid="quiz-next-button"
              >
                {idx + 1 >= total ? "Build my priority map →" : "Next →"}
              </button>
              <button type="button" className="btn-outline-navy" onClick={back} data-testid="quiz-back-button">
                ← Back
              </button>
            </div>
          </div>
        </section>
      )}

      {mode === "loading" && <LoadingScreen brandName="your business" />}

      {mode === "report" && report && (
        <AssessmentReport report={report} dateLabel={fmtDate(new Date().toISOString())} isPast={false} onRetake={start} />
      )}

      {mode === "past" && pastReport && (
        <AssessmentReport
          report={pastReport}
          dateLabel={fmtDate(pastDate)}
          isPast={true}
          onRetake={start}
          onBackToIntro={() => { setMode("intro"); setPastReport(null); setPastDate(null); }}
        />
      )}
    </div>
  );
};

const AssessmentIntro = ({ onStart, history, onViewPast }) => {
  const hasHistory = history && history.length > 0;
  return (
    <section style={{ padding: "3rem 0 4rem" }}>
      <div className="container-narrow">
        <p className="eyebrow">Start here</p>
        <h1 style={{ fontSize: "clamp(2rem, 5vw, 2.6rem)", margin: "0.3rem 0 1.2rem", letterSpacing: "-0.02em" }} data-testid="quiz-intro-title">
          The Legal IQ Assessment
        </h1>
        <div style={{ fontSize: "1.05rem", lineHeight: 1.65, color: "var(--midnight)" }}>
          <p>{hasHistory ? "Welcome back." : "Hey, glad you're here."}</p>
          <p>This isn't a test. There are no right or wrong answers. I just want a 30-second read on your business — the same questions I'd ask a new client on a discovery call.</p>
          <p>9 quick questions. About 3 minutes.</p>
          <p>At the end, I'll build you a personalized <strong>Brand Protection Priority Map</strong>: what to protect first, what to watch, and what you're already doing right.</p>
          <p style={{ color: "var(--cobalt)", fontWeight: 700 }}>Pro tip: take this every quarter. Your priorities shift as your business grows — and the map shifts with you.</p>
        </div>
        <button type="button" className="btn-yellow" onClick={onStart} data-testid="quiz-start-button" style={{ marginTop: "1.5rem" }}>
          {hasHistory ? "Take it again →" : "Start the Assessment →"}
        </button>

        {hasHistory && (
          <div style={{ marginTop: "3rem" }} data-testid="quiz-history-section">
            <h2 style={{ fontSize: "1.05rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--navy)", borderBottom: "2px solid var(--yellow)", paddingBottom: "0.4rem", margin: "0 0 1.1rem" }}>
              Your Brand Protection trajectory
            </h2>
            <p style={{ margin: "0 0 0.4rem", color: "var(--midnight)" }}>
              {history.length === 1
                ? "Your most recent Priority Map. Take it again to see what's shifted."
                : `You've taken this ${history.length} times. Here's how your brand exposure has shifted:`}
            </p>
            <p style={{ margin: "0 0 1rem", color: "var(--cobalt)", fontSize: "0.88rem" }}>
              Exposure = how much risk your brand has of someone else claiming it. Lower is better.
            </p>

            {/* Visual trajectory chips */}
            {history.length > 1 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center", marginBottom: "1.2rem" }} data-testid="quiz-trajectory-chips">
                {[...history].reverse().map((h, i) => {
                  const meta = EXPOSURE_META[h.exposure_level] || EXPOSURE_META.medium;
                  return (
                    <div key={h.id} style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                      <span
                        title={`Brand exposure: ${meta.label}`}
                        style={{
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          padding: "0.25rem 0.55rem",
                          background: meta.color,
                          color: "var(--warm-white)",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {meta.label} EXPOSURE
                      </span>
                      {i < history.length - 1 && <span style={{ color: "var(--cobalt)", fontWeight: 700 }}>→</span>}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
              {history.map((h) => {
                const meta = EXPOSURE_META[h.exposure_level] || EXPOSURE_META.medium;
                return (
                  <button
                    key={h.id}
                    type="button"
                    onClick={() => onViewPast(h.id)}
                    data-testid={`quiz-history-item-${h.id}`}
                    style={{
                      textAlign: "left",
                      background: "var(--warm-white)",
                      border: "2px solid var(--navy)",
                      borderLeft: `8px solid ${meta.color}`,
                      padding: "0.9rem 1rem",
                      cursor: "pointer",
                      font: "inherit",
                      color: "inherit",
                      boxShadow: "3px 3px 0 0 var(--navy)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.3rem", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "0.2rem 0.5rem", background: meta.color, color: "var(--warm-white)", letterSpacing: "0.05em" }}>
                        {meta.label} EXPOSURE
                      </span>
                      <span style={{ color: "var(--cobalt)", fontWeight: 700, fontSize: "0.85rem" }}>
                        {fmtDate(h.created_at)}
                      </span>
                      {h.protect_first_asset && (
                        <span style={{ color: "var(--midnight)", fontSize: "0.85rem" }}>
                          · Protect first: <strong>{h.protect_first_asset}</strong>
                        </span>
                      )}
                    </div>
                    {h.headline && (
                      <div style={{ color: "var(--midnight)", fontSize: "0.95rem", lineHeight: 1.45 }}>
                        {h.headline}
                      </div>
                    )}
                    <div style={{ marginTop: "0.4rem", color: "var(--cobalt)", fontWeight: 700, fontSize: "0.85rem" }}>
                      View this Priority Map →
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const AssessmentReport = ({ report, dateLabel, isPast, onRetake, onBackToIntro }) => {
  const meta = EXPOSURE_META[report.exposure_level] || EXPOSURE_META.medium;
  return (
    <section style={{ padding: "2.5rem 0 4rem" }} data-testid="quiz-results">
      <div className="container-narrow">
        {isPast && (
          <div
            className="callout"
            style={{ marginBottom: "1.5rem", borderTopColor: "var(--cobalt)" }}
            data-testid="quiz-viewing-past-banner"
          >
            <p style={{ margin: 0 }}>
              <strong>Viewing your Priority Map from {dateLabel}.</strong>{" "}
              {onBackToIntro && (
                <button
                  type="button"
                  onClick={onBackToIntro}
                  style={{ background: "none", border: "none", padding: 0, color: "var(--cobalt)", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
                  data-testid="quiz-back-to-intro"
                >
                  ← Back to all
                </button>
              )}
            </p>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
          <p className="eyebrow" style={{ margin: 0 }}>Your Brand Protection Priority Map</p>
          <span
            style={{ fontSize: "0.7rem", fontWeight: 700, padding: "0.25rem 0.55rem", background: meta.color, color: "var(--warm-white)", letterSpacing: "0.05em" }}
            data-testid="quiz-exposure-chip"
          >
            EXPOSURE: {meta.label}
          </span>
          <span style={{ color: "var(--cobalt)", fontSize: "0.85rem" }}>· {dateLabel}</span>
        </div>

        <h1
          style={{ fontSize: "clamp(1.7rem, 4.5vw, 2.3rem)", margin: "0.3rem 0 1.8rem", letterSpacing: "-0.01em", lineHeight: 1.25 }}
          data-testid="quiz-result-title"
        >
          {report.headline}
        </h1>

        {/* Whats shifted block — only on return visits */}
        {report.whats_shifted && report.whats_shifted.trim() && (
          <div
            className="callout"
            style={{ marginBottom: "1.8rem", borderTopColor: "var(--evergreen)", background: "rgba(0,103,71,0.06)" }}
            data-testid="quiz-whats-shifted"
          >
            <p className="eyebrow" style={{ color: "var(--evergreen)", margin: "0 0 0.4rem" }}>What's shifted since last time</p>
            <p style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{report.whats_shifted}</p>
          </div>
        )}

        {/* Priority cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "2rem" }}>
          <PriorityCard
            tone="protect"
            eyebrow="PROTECT FIRST"
            title={report.protect_first?.asset}
            body={report.protect_first?.why}
            testId="quiz-priority-protect"
          />
          <PriorityCard
            tone="watch"
            eyebrow="WATCH CLOSELY"
            title={report.watch_closely?.area}
            body={report.watch_closely?.why}
            testId="quiz-priority-watch"
          />
          {report.already_ahead_on && report.already_ahead_on.trim() && (
            <PriorityCard
              tone="ahead"
              eyebrow="ALREADY AHEAD ON"
              title="What's working for you"
              body={report.already_ahead_on}
              testId="quiz-priority-ahead"
            />
          )}
        </div>

        {/* Insight paragraph — tinted panel for visual rhythm */}
        <div
          style={{
            background: "rgba(20, 27, 77, 0.04)",
            border: "1px solid rgba(20, 27, 77, 0.12)",
            padding: "1.4rem 1.4rem 1rem",
            marginBottom: "1.8rem",
          }}
        >
          <h2 style={{ fontSize: "1.05rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--navy)", borderBottom: "2px solid var(--yellow)", paddingBottom: "0.4rem", margin: "0 0 0.85rem" }}>
            What I see going on here
          </h2>
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.65 }} data-testid="quiz-insight-paragraph">
            {report.insight_paragraph}
          </div>
        </div>

        {/* Blind spot callout */}
        {report.blind_spot_callout && report.blind_spot_callout.trim() && (
          <div
            className="callout"
            style={{ marginTop: "0.5rem", marginBottom: "1.8rem", borderTopColor: "var(--yellow)" }}
            data-testid="quiz-blind-spot"
          >
            <p className="eyebrow" style={{ color: "var(--cobalt)", margin: "0 0 0.4rem" }}>The easiest move you can make this week</p>
            <p style={{ margin: 0 }}>{report.blind_spot_callout}</p>
          </div>
        )}

        {/* Soft bridge into the Audit */}
        <div className="callout" style={{ marginTop: "1rem", marginBottom: "1.5rem" }}>
          <p style={{ margin: 0 }} data-testid="quiz-next-step-summary">{report.next_step_summary}</p>
        </div>

        {/* Disclaimer */}
        <p style={{ fontStyle: "italic", color: "var(--cobalt)", fontSize: "0.92rem", margin: "0 0 0" }} data-testid="quiz-disclaimer">
          {report.disclaimer}
        </p>
      </div>

      {/* What's next — navy CTA band */}
      <div
        className="brand-navy-band"
        style={{ padding: "3rem 0 3.5rem", marginTop: "3rem", color: "var(--warm-white)" }}
        data-testid="quiz-whats-next-band"
      >
        <div className="container-narrow">
          <p className="eyebrow" style={{ color: "var(--yellow)" }}>What's next</p>
          <h2 style={{ color: "var(--warm-white)", margin: "0.4rem 0 1.5rem", fontSize: "clamp(1.4rem, 3.5vw, 1.9rem)" }}>
            Pick your next move.
          </h2>

          <div style={{ display: "flex", flexDirection: "column", gap: "1.8rem" }}>
            {isPast && (
              <div style={{ borderLeft: "4px solid var(--yellow)", paddingLeft: "1rem" }}>
                <p style={{ margin: "0 0 0.5rem", fontWeight: 700, color: "var(--warm-white)" }}>Run it again</p>
                <p style={{ margin: "0 0 0.7rem", color: "rgba(242,233,219,0.85)" }}>
                  Your situation shifts as your business grows. Take it again and I'll show you exactly what's moved.
                </p>
                <button type="button" onClick={onRetake} className="btn-yellow" data-testid="quiz-retake-button">
                  Take it again to see what's shifted →
                </button>
              </div>
            )}

            <div style={{ borderLeft: "4px solid var(--yellow)", paddingLeft: "1rem" }}>
              <p style={{ margin: "0 0 0.5rem", fontWeight: 700, color: "var(--warm-white)" }}>Take the deeper read</p>
              <p style={{ margin: "0 0 0.7rem", color: "rgba(242,233,219,0.85)" }}>
                I'll put your specific brand under the microscope and build a personalized Action Plan: where you're protected, where you're exposed, and what to do this month.
              </p>
              <Link to="/audit" className={isPast ? "btn-outline-yellow" : "btn-yellow"} data-testid="quiz-audit-cta">
                Run the Brand Protection Audit →
              </Link>
            </div>

            <div style={{ borderLeft: "4px solid var(--yellow)", paddingLeft: "1rem" }}>
              <p style={{ margin: "0 0 0.5rem", fontWeight: 700, color: "var(--warm-white)" }}>Go to the source</p>
              <p style={{ margin: "0 0 0.7rem", color: "rgba(242,233,219,0.85)" }}>
                "Protect Your Brand & Get Legally Legit" — my free, live training on calling dibs on your trademarks. Step-by-step.
              </p>
              <a
                href="https://indielaw.easywebinar.live/gettrademarked"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-yellow"
                data-testid="quiz-webinar-cta"
              >
                Save my spot for the free training →
              </a>
            </div>

            <div style={{ borderLeft: "4px solid var(--yellow)", paddingLeft: "1rem" }}>
              <p style={{ margin: "0 0 0.5rem", fontWeight: 700, color: "var(--warm-white)" }}>Go deeper anytime</p>
              <p style={{ margin: 0, color: "rgba(242,233,219,0.85)" }}>
                My book <em>Legally Legit</em> covers all of this and more.{" "}
                <a
                  href="https://a.co/d/0isDjKfG"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--yellow)", fontWeight: 700 }}
                  data-testid="quiz-book-link"
                >
                  Get it on Amazon →
                </a>
              </p>
            </div>
          </div>

          <div style={{ marginTop: "2.2rem", textAlign: "center" }}>
            <Link
              to="/dashboard"
              data-testid="quiz-back-to-dashboard"
              style={{ color: "rgba(242,233,219,0.85)", fontWeight: 700 }}
            >
              ← Back to dashboard
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const TONE_STYLES = {
  protect: { dot: "var(--cobalt)", border: "var(--cobalt)" },
  watch:   { dot: "var(--yellow)", border: "var(--yellow)" },
  ahead:   { dot: "var(--evergreen)", border: "var(--evergreen)" },
};

const PriorityCard = ({ tone, eyebrow, title, body, testId }) => {
  const s = TONE_STYLES[tone] || TONE_STYLES.protect;
  return (
    <div
      data-testid={testId}
      style={{
        background: "var(--warm-white)",
        border: "2px solid var(--navy)",
        borderLeft: `8px solid ${s.border}`,
        padding: "1.1rem 1.2rem",
        boxShadow: "4px 4px 0 0 var(--navy)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", marginBottom: "0.4rem" }}>
        <span
          aria-hidden="true"
          style={{ width: 10, height: 10, background: s.dot, borderRadius: "50%", display: "inline-block" }}
        />
        <span className="eyebrow" style={{ color: "var(--navy)", margin: 0 }}>{eyebrow}</span>
      </div>
      {title && (
        <h3 style={{ margin: "0 0 0.4rem", fontSize: "1.15rem", color: "var(--navy)" }}>
          {title}
        </h3>
      )}
      <p style={{ margin: 0, color: "var(--midnight)", lineHeight: 1.55 }}>{body}</p>
    </div>
  );
};
